import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal, Space, Spin, Table } from 'antd';
import useIsMobile from '../../../hooks/useIsMobile';
import pairTradingApi, {
  PairImportPreview,
  PairImportStockLeg,
} from '../../../services/pairTradingApi';
import { importInProgressOf } from '../../../services/importJobApi';
import ImportJobProgress from '../../../components/ImportJobProgress';
import { formatDecimal, formatInt, numberInputProps } from '../../../utils/numberFormat';
import { Field, FieldGrid, NUMBER_INPUT_STYLE, ReadonlyValue, RightAlignedInputs, SectionTitle } from '../../../components/ImportForm';
import { formatIsoDate, normalisedWeights, pairImportBlockers } from '../../../utils/rollPlanView';
import { PAIR_IMPORT_LOT, splitPairImport } from '../../../utils/pairImportSplit';

/**
 * Rebuild the pair from what the broker actually holds.
 *
 * Reached from the orphaned-leg banner: expiry passed with no roll, the exchange
 * cash-settled the futures leg, and the recorded pair now describes positions
 * that do not exist. Nothing else recovers from that — a roll needs a live
 * contract on both sides, and a close needs something to send an order to.
 *
 * Two screens on purpose. The first is the server's preview: what gets archived
 * (with the money on it), which engine keys get purged, and what the broker
 * reports right now. The second is the basket that will be created, pre-filled
 * from that snapshot and editable, behind a typed confirmation: k trading pairs of
 * one contract each, every stock leg split evenly over them in lots of 100. The
 * import then runs as a background job whose progress replaces the form. Nothing is
 * deleted at any point — the old legs are archived with their transaction
 * history — but the pair the bot trades is replaced, so it is worth reading.
 */
export interface PairImportModalProps {
  open: boolean;
  onClose: () => void;
  dataConfigId: string | null;
  /** Fired once the import job is READY, with the pair it created, so the page can select it. */
  onImported: (newDataConfigId: string | null) => void;
  /** The pair is stuck in ENDING: it may also be ended here without a replacement. */
  stuckEnding?: boolean;
  /** Fired after the pair was ended without a replacement. */
  onEnded?: () => void;
}

const GREY = '#848E9C';

const PairImportModal: React.FC<PairImportModalProps> = ({
  open,
  onClose,
  dataConfigId,
  onImported,
  stuckEnding = false,
  onEnded,
}) => {
  const [preview, setPreview] = useState<PairImportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [legs, setLegs] = useState<PairImportStockLeg[]>([]);
  const [stockCash, setStockCash] = useState<number>(0);
  const [derivCash, setDerivCash] = useState<number>(0);
  const [referencePrice, setReferencePrice] = useState<number | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [endConfirm, setEndConfirm] = useState('');
  const [endingPair, setEndingPair] = useState(false);
  const [pairs, setPairs] = useState<number>(0);
  // the job the import / end became; set, its progress replaces the form
  const [job, setJob] = useState<{ id: string; kind: 'import' | 'end'; note: string | null } | null>(null);
  const [jobDone, setJobDone] = useState(false);

  const load = useCallback(async () => {
    if (!dataConfigId) return;
    setLoading(true);
    setError(null);
    try {
      const next = await pairTradingApi.previewPairImport(dataConfigId);
      setPreview(next);
      setLegs(next.proposal.stock_legs);
      setStockCash(next.proposal.stock_allocated_cash);
      setDerivCash(next.proposal.deriv_allocated_cash);
      setReferencePrice(next.proposal.future_instrument_price);
      setPairs(Math.min(next.proposal.pairs, next.proposal.free_futures));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'could not read the broker snapshot');
    } finally {
      setLoading(false);
    }
  }, [dataConfigId]);

  useEffect(() => {
    if (!open) {
      setJob(null);
      setJobDone(false);
      setConfirmText('');
      setEndConfirm('');
      return;
    }
    void load();
  }, [open, load]);

  // what the broker holds free of other bots, per proposed symbol: the ceiling on each leg
  const freeBySymbol = useMemo(
    () => new Map((preview?.proposal.stock_legs ?? []).map((l) => [l.symbol, l.qty])),
    [preview],
  );
  const split = useMemo(
    () =>
      splitPairImport(
        legs.map((l) => ({ symbol: l.symbol, qty: l.qty, free: freeBySymbol.get(l.symbol) })),
        pairs,
        preview?.proposal.free_futures,
      ),
    [legs, pairs, freeBySymbol, preview],
  );
  const blockers = [...pairImportBlockers(preview, legs, confirmText), ...split.problems];
  const percents = normalisedWeights(legs.map((l) => ({ symbol: l.symbol, weight: l.weight })));

  const submit = async () => {
    if (!dataConfigId || !preview) return;
    setSubmitting(true);
    setError(null);
    try {
      const { importId } = await pairTradingApi.applyPairImport(dataConfigId, {
        confirm: confirmText.trim(),
        stock_legs: legs,
        pairs,
        deriv_symbol: preview.proposal.deriv_symbol,
        stock_allocated_cash: stockCash,
        deriv_allocated_cash: derivCash,
        future_instrument_price: referencePrice ?? undefined,
      });
      setJob({ id: importId, kind: 'import', note: null });
    } catch (e: any) {
      const open409 = importInProgressOf(e);
      if (open409) {
        setJob({ id: open409.importId, kind: 'import', note: open409.message });
        return;
      }
      setError(e?.response?.data?.message ?? e?.message ?? 'the import failed');
    } finally {
      setSubmitting(false);
    }
  };

  const updateLeg = (index: number, patch: Partial<PairImportStockLeg>) => {
    setLegs((prev) => prev.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)));
  };

  const endPair = async () => {
    if (!dataConfigId) return;
    setEndingPair(true);
    setError(null);
    try {
      const { importId } = await pairTradingApi.endPairFromBroker(dataConfigId, endConfirm.trim());
      setJob({ id: importId, kind: 'end', note: null });
    } catch (e: any) {
      const open409 = importInProgressOf(e);
      if (open409) {
        setJob({ id: open409.importId, kind: 'end', note: open409.message });
        return;
      }
      setError(e?.response?.data?.message ?? e?.message ?? 'the pair could not be ended');
    } finally {
      setEndingPair(false);
    }
  };

  const isMobile = useIsMobile();
  // as a grid symbol's End from broker: offered while the pair still holds something, or is stuck ending
  const holdsPositions =
    !!preview &&
    (preview.current.openBookPairs > 0 || preview.current.legs.some((leg) => Number(leg.heldQty) > 0));
  const canEndFromBroker = stuckEnding || holdsPositions;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Import the pair from broker positions"
      footer={null}
      width={isMobile ? 'calc(100vw - 16px)' : 760}
      style={isMobile ? { top: 8, maxWidth: 'calc(100vw - 16px)' } : undefined}
      destroyOnClose
    >
      {loading && !preview ? (
        <Spin />
      ) : job ? (
        <div style={{ fontSize: 12, color: '#EAECEF', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {job.note && <Alert type="warning" showIcon style={{ fontSize: 11 }} message={job.note} />}
          <span style={{ color: GREY }}>
            {job.kind === 'end'
              ? 'Ending the pair in the background: the database and the bot change together, or neither does.'
              : 'Importing in the background: the database and the bot change together, or neither does. Closing this window does not stop it.'}
          </span>
          <ImportJobProgress
            importId={job.id}
            onReady={(view) => {
              setJobDone(true);
              // an attached 409 may be either kind: the job says which
              if (view.kind === 'PAIR_END') onEnded?.();
              else onImported(view.resultDataConfigId);
            }}
          />
          {jobDone && job.kind === 'import' && (
            <span style={{ color: GREY }}>
              The old pair was archived and a new one created. It starts PAUSED, with the roll set back to automatic.
            </span>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type={jobDone ? 'primary' : 'default'} onClick={onClose}>{jobDone ? 'Done' : 'Close'}</Button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: '#EAECEF', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {preview?.current.derivExpired && (
            <Alert
              type="error"
              showIcon
              style={{ fontSize: 11 }}
              message={`${preview.current.derivSymbol} expired on ${formatIsoDate(preview.current.derivExpiry)}`}
              description={
                `The exchange has cash-settled that leg. The recorded pair still carries ` +
                `${preview.current.openBookPairs} pair(s) in the bot's book and ` +
                `${formatInt(Number(preview.current.totalCommitted))} VND of allocated cash.`
              }
            />
          )}

          {(preview?.warnings ?? []).map((warning) => (
            <Alert key={warning} type="warning" showIcon style={{ fontSize: 11 }} message={warning} />
          ))}

          <div>
            <SectionTitle>Archived (nothing is deleted)</SectionTitle>
            <Table
              size="small"
              pagination={false}
              rowKey="instrumentConfigId"
              scroll={{ x: 'max-content' }}
              dataSource={preview?.current.legs ?? []}
              columns={[
                { title: 'Leg', dataIndex: 'leg', width: 70 },
                {
                  title: 'Symbol',
                  render: (_: unknown, row: any) => `${row.exchange}:${row.symbol}`,
                },
                {
                  title: 'Held',
                  dataIndex: 'heldQty',
                  align: 'right' as const,
                  render: (v: string) => formatInt(Number(v)),
                },
                {
                  title: 'Cash (VND)',
                  dataIndex: 'currentCapital',
                  align: 'right' as const,
                  render: (v: string) => formatInt(Number(v)),
                },
                { title: 'Status', dataIndex: 'status', width: 90 },
              ]}
            />
          </div>

          <div>
            <SectionTitle hint="Pre-filled from the positions your broker reports. You choose how many shares to claim; their cost is always the broker's, so the new pair starts knowing what it owns.">
              Imported basket
            </SectionTitle>
            <RightAlignedInputs>
            <Table
              size="small"
              pagination={false}
              rowKey={(row: PairImportStockLeg) => row.symbol}
              scroll={{ x: 'max-content' }}
              dataSource={legs}
              columns={[
                {
                  title: 'Symbol',
                  render: (_: unknown, row: PairImportStockLeg, index: number) => (
                    <Input
                      size="small"
                      value={row.symbol}
                      onChange={(e) => updateLeg(index, { symbol: e.target.value.toUpperCase() })}
                      style={{ width: 96 }}
                    />
                  ),
                },
                {
                  // the basket's own name for it in Trading Parameters
                  title: 'Weight',
                  align: 'right' as const,
                  render: (_: unknown, row: PairImportStockLeg, index: number) => (
                    <InputNumber
                      size="small"
                      min={0}
                      max={1}
                      step={0.05}
                      value={row.weight}
                      onChange={(v) => updateLeg(index, { weight: v ?? 0 })}
                      style={{ width: 84 }}
                    />
                  ),
                },
                {
                  title: 'Share',
                  align: 'right' as const,
                  render: (_: unknown, row: PairImportStockLeg) => {
                    const found = percents.find((p) => p.symbol === row.symbol);
                    return `${formatDecimal(found ? found.percent : 0, 1)}%`;
                  },
                },
                {
                  title: 'Free',
                  align: 'right' as const,
                  render: (_: unknown, row: PairImportStockLeg) => {
                    const free = freeBySymbol.get(row.symbol);
                    return <span style={{ color: GREY }}>{free !== undefined ? formatInt(free) : '—'}</span>;
                  },
                },
                {
                  title: 'Shares to import',
                  align: 'right' as const,
                  render: (_: unknown, row: PairImportStockLeg, index: number) => (
                    <InputNumber
                      size="small"
                      min={0}
                      max={freeBySymbol.get(row.symbol)}
                      step={PAIR_IMPORT_LOT}
                      value={row.qty}
                      onChange={(v) => updateLeg(index, { qty: Math.max(0, Math.round(Number(v ?? 0))) })}
                      {...numberInputProps()}
                      style={{ width: 110 }}
                    />
                  ),
                },
                {
                  title: 'Per pair',
                  align: 'right' as const,
                  render: (_: unknown, _row: PairImportStockLeg, index: number) => {
                    const leg = split.legs[index];
                    if (!leg) return '—';
                    return (
                      <span data-testid={`per-pair-${index}`} style={{ color: leg.problem ? '#F6465D' : undefined }}>
                        {formatInt(leg.perPair)}
                      </span>
                    );
                  },
                },
                {
                  title: 'Left unclaimed',
                  align: 'right' as const,
                  render: (_: unknown, _row: PairImportStockLeg, index: number) => {
                    const leg = split.legs[index];
                    return <span data-testid={`unclaimed-${index}`}>{leg ? formatInt(leg.unclaimed) : '—'}</span>;
                  },
                },
                {
                  title: '',
                  width: 40,
                  render: (_: unknown, _row: PairImportStockLeg, index: number) => (
                    <Button
                      size="small"
                      type="text"
                      danger
                      onClick={() => setLegs((prev) => prev.filter((__, i) => i !== index))}
                    >
                      ✕
                    </Button>
                  ),
                },
              ]}
            />
            </RightAlignedInputs>
            <Button
              size="small"
              style={{ marginTop: 8 }}
              onClick={() => setLegs((prev) => [...prev, { symbol: '', weight: 0.1, qty: 0 }])}
            >
              Add symbol
            </Button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldGrid columns={3}>
              <Field
                label="New futures contract"
                help={`Free contracts: ${formatInt(preview?.proposal.free_futures ?? 0)}`}
              >
                <ReadonlyValue strong>{preview?.proposal.deriv_symbol ?? '—'}</ReadonlyValue>
              </Field>
              <Field
                label="Number of pairs"
                hint="Each trading pair is one futures contract (short) plus its share of every stock leg. Each leg is split evenly over the pairs in lots of 100; what does not divide stays unclaimed at the broker. 0 rebuilds the pair flat."
              >
                <InputNumber
                  aria-label="Number of pairs"
                  style={NUMBER_INPUT_STYLE}
                  min={0}
                  max={preview?.proposal.free_futures ?? 0}
                  precision={0}
                  value={pairs}
                  onChange={(v) => setPairs(Math.max(0, Math.round(Number(v ?? 0))))}
                />
              </Field>
              <Field
                label="Futures Target Price"
                hint="The OPEN trigger compares the live price against this. It belongs to the NEW contract — the old one no longer quotes, so it cannot be carried over automatically. Can be filled in later, but the pair will not start without it."
              >
                <InputNumber
                  style={NUMBER_INPUT_STYLE}
                  min={0}
                  step={0.1}
                  value={referencePrice}
                  onChange={(v) => setReferencePrice(v ?? null)}
                />
              </Field>
            </FieldGrid>
            <FieldGrid columns={2}>
              <Field
                label="Stock cash (VND)"
                help={preview?.broker.stock ? `Free after reset: ${formatInt(preview.broker.stock.allocatableAfterReset ?? 0)}` : undefined}
              >
                <InputNumber
                  {...numberInputProps()}
                  style={NUMBER_INPUT_STYLE}
                  min={0}
                  value={stockCash}
                  onChange={(v) => setStockCash(v ?? 0)}
                />
              </Field>
              <Field
                label="Futures cash (VND)"
                help={preview?.broker.deriv ? `Free after reset: ${formatInt(preview.broker.deriv.allocatableAfterReset ?? 0)}` : undefined}
              >
                <InputNumber
                  {...numberInputProps()}
                  style={NUMBER_INPUT_STYLE}
                  min={0}
                  value={derivCash}
                  onChange={(v) => setDerivCash(v ?? 0)}
                />
              </Field>
            </FieldGrid>
          </div>

          <Alert
            type="warning"
            showIcon
            style={{ fontSize: 11 }}
            message="This replaces the pair the bot trades"
            description={
              `The current legs are archived (status CLOSED, allocated cash released to the ledger) ` +
              `and the bot's book is rebuilt from this basket in the same step. History is kept; ` +
              `the running configuration is not.`
            }
          />

          {canEndFromBroker && (
            <Alert
              type="info"
              showIcon
              style={{ fontSize: 11 }}
              message="Or end the pair without a replacement"
              description={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span>
                    {stuckEnding
                      ? "The pair is stuck ending: a leg still counts shares the broker no longer holds. "
                      : 'Nothing is sold: use this when the bot cannot close the pair itself (e.g. an order stuck at the broker). '}
                    Ending it archives every leg and returns what each was given to the account's free cash. Shares and
                    contracts the broker still holds belong to no bot afterwards: close them on the price board.
                  </span>
                  <Space wrap>
                    <Input
                      value={endConfirm}
                      onChange={(e) => setEndConfirm(e.target.value)}
                      placeholder="Type END to confirm"
                      style={{ width: isMobile ? '100%' : 180 }}
                    />
                    <Button danger loading={endingPair} disabled={endConfirm.trim() !== 'END' || submitting} onClick={() => void endPair()}>
                      End the pair
                    </Button>
                  </Space>
                </div>
              }
            />
          )}

          <div>
            <div style={{ color: GREY, marginBottom: 4 }}>
              {`Type ${preview?.confirmationPhrase ?? 'IMPORT'} to confirm`}
            </div>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={preview?.confirmationPhrase ?? 'IMPORT'}
              style={{ width: isMobile ? '100%' : 220 }}
            />
          </div>

          {error && <Alert type="error" showIcon message={error} />}
          {blockers.length > 0 && (
            <div style={{ color: GREY, fontSize: 11 }}>{blockers.join(' · ')}</div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Space wrap style={{ justifyContent: 'flex-end' }}>
              <Button onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button onClick={() => void load()} disabled={submitting || loading}>Refresh snapshot</Button>
              <Button
                danger
                type="primary"
                loading={submitting}
                disabled={blockers.length > 0}
                onClick={submit}
              >
                Archive and import
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PairImportModal;
